import { Add, Subtract } from '@withjoy/joykit/icons';
import { useTranslation } from '@shared/core';
import { Box, ButtonV2, Divider, Flex, FormControl, PopoverV2, TextV2, useDisclosure } from '@withjoy/joykit';
import { Profile, Trash } from '@withjoy/joykit/icons';
import { pxToRem } from '@withjoy/joykit/theme';
import { useField, useFormikContext } from 'formik';
import React from 'react';
import { useSearchFormConfig } from '../useSearchFormConfig';
import { createOccupancySchema, AGE_OPTIONS, StyledAgeSelect } from '@apps/stays/hooks/occupancySchema';

// TODO debug Yup.InferType<typeof schema> to get the correct type that works with eslint
type ISchema = {
  adults: number;
  childrenAges: (number | null)[];
}[];

const createSchema = createOccupancySchema;

// Type definitions
type IRoomSchema = ISchema[0];
type IRoomsSchema = ISchema;

// TODO: type this based on schema
const initialValue = [
  {
    adults: 2,
    childrenAges: [],
  },
];

const numberOfRooms = (rooms: IRoomsSchema) => rooms.length;
const numberOfAdults = (rooms: IRoomsSchema) => rooms.reduce((acc, room) => acc + room.adults, 0);
const numberOfChildren = (rooms: IRoomsSchema) => rooms.reduce((acc, room) => acc + room.childrenAges.filter(age => age !== null).length, 0);

const Fields = ({ fieldsId }: { fieldsId: string }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  const { isOpenedFormView, isCompactVersion } = useSearchFormConfig();
  const [_, meta] = useField<ISchema>(fieldsId);
  const { isOpen, onOpen, onClose } = useDisclosure();
  // Yup is really struggling with the schema inference for Yup.InferType<typeof schema>
  const [rooms] = useField<IRoomsSchema>(`${fieldsId}`);
  const errorProps = {
    isInvalid: meta.touched && !!meta.error,
    error: meta.touched && typeof meta.error === 'string' ? meta.error : undefined,
  };

  return (
    <FormControl
      {...errorProps}
      paddingY={isCompactVersion ? 2 : { _: 2, md: 0 }}
    >
      {isOpenedFormView ? (
        <Rooms
          fieldsId={`${fieldsId}`}
          onClose={onClose}
        />
      ) : (
        <PopoverV2
          isOpen={isOpen}
          onOpen={onOpen}
          onClose={onClose}
          placement={'bottomEnd'}
          overrides={{
            Content: {
              props: {
                borderRadius: pxToRem(12),
              },
            },
          }}
        >
          <PopoverV2.Trigger>
            <Box
              display={'flex'}
              gap={3}
              alignItems={'center'}
              cursor="pointer"
              paddingX={4}
            >
              <Profile />
              <Box typographyVariant={'label2'}>
                {tq.plural(numberOfRooms(rooms.value), {
                  one: tq`1 Room`,
                  other: tq`${numberOfRooms(rooms.value)} Rooms`,
                })}
                ,{' '}
                {tq.plural(numberOfAdults(rooms.value) + numberOfChildren(rooms.value), {
                  one: tq`1 Guest`,
                  other: tq`${numberOfAdults(rooms.value) + numberOfChildren(rooms.value)} Guests`,
                })}
              </Box>
            </Box>
          </PopoverV2.Trigger>
          <PopoverV2.Content>
            <Rooms
              fieldsId={`${fieldsId}`}
              onClose={onClose}
            />
          </PopoverV2.Content>
        </PopoverV2>
      )}
    </FormControl>
  );
};

const Rooms = ({ fieldsId, onClose }: { fieldsId: string; onClose?: () => void }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  const { isOpenedFormView, isCompactVersion } = useSearchFormConfig();
  const [field, _, helper] = useField<IRoomsSchema>(fieldsId);
  const { validateForm, setFieldTouched } = useFormikContext();
  const handleAddRoom = () => {
    helper.setValue([...field.value, { adults: 2, childrenAges: [] }]);
  };
  const handleRemoveRoom = (index: number) => {
    helper.setValue(field.value.filter((_, i) => i !== index));
  };

  const handleApplyOccupancy = async () => {
    field.value.forEach((room, roomIndex) => {
      room.childrenAges.forEach((_, childIndex) => {
        setFieldTouched(`${fieldsId}[${roomIndex}].childrenAges[${childIndex}]`, true, false);
      });
    });

    const validationErrors = await validateForm();
    const hasErrors = validationErrors && Object.prototype.hasOwnProperty.call(validationErrors, fieldsId);

    if (!hasErrors) {
      onClose?.();
    }
  };
  const numberOfRooms = field.value.length;
  return (
    <Box
      rowGap={4}
      padding={isCompactVersion ? 4 : { _: isOpenedFormView ? 0 : 4, md: 4 }}
      minWidth={isCompactVersion ? 'full' : { _: isOpenedFormView ? 'full' : 350, md: 350 }}
      maxHeight={isCompactVersion ? 'full' : { _: isOpenedFormView ? 400 : 'full', md: 400 }}
      overflow={'auto'}
    >
      {isOpenedFormView ? (
        <TextV2
          typographyVariant="hed3"
          marginBottom={4}
        >
          {tq`Select Room`}
        </TextV2>
      ) : (
        <>
          <Box
            typographyVariant="hed2"
            marginBottom={2}
            padding={1}
          >
            {tq`Configuring Rooms`}
          </Box>
          <Divider marginBottom={2} />
        </>
      )}
      {Array.from({ length: field.value.length }, (_, index) => (
        <>
          <RoomFields
            key={index}
            fieldsId={`${fieldsId}[${index}]`}
            onRemoveRoom={numberOfRooms > 1 ? handleRemoveRoom.bind(null, index) : undefined}
            roomNumber={index + 1}
          />
          {index < numberOfRooms - 1 && <Divider marginTop={2} />}
        </>
      ))}
      <Flex
        width="full"
        justifyContent="space-between"
        marginTop={5}
        gap={5}
        paddingX={2}
      >
        <ButtonV2
          key={'add-room'}
          height={40}
          width={'full'}
          _hover={{ backgroundColor: { _: 'white', md: '#4D4D4D1A' }, borderColor: { _: 'mono14', md: 'mono12' }, color: { _: 'mono14', md: 'mono12' } }}
          shape="rounded"
          variant="outline"
          intent="neutral"
          onClick={handleAddRoom}
        >
          {tq`Add Room`}
        </ButtonV2>
        {!isOpenedFormView && (
          <ButtonV2
            key={'done'}
            height={40}
            width={'full'}
            shape="rounded"
            variant="solid"
            intent="neutral"
            onClick={handleApplyOccupancy}
          >
            {tq`Apply`}
          </ButtonV2>
        )}
      </Flex>
    </Box>
  );
};

const RoomFields = ({ fieldsId, onRemoveRoom, roomNumber }: { fieldsId: string; onRemoveRoom?: () => void; roomNumber: number }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  return (
    <Box
      display={'grid'}
      gridTemplateColumns={'1fr'}
      rowGap={3}
    >
      <Flex
        alignItems="center"
        justifyContent={'space-between'}
        width="full"
      >
        <Box
          typographyVariant={'hed1'}
          fontWeight={600}
        >
          {tq`Room ${roomNumber}`}
        </Box>
        {onRemoveRoom && (
          <ButtonV2
            typographyVariant={'label2'}
            startIcon={<Trash size={16} />}
            variant="ghost"
            intent="destructive"
            onClick={onRemoveRoom}
            justifySelf={'start'}
            paddingX={0}
          >
            {tq`Remove Room`}
          </ButtonV2>
        )}
      </Flex>
      <NumAdults fieldsId={`${fieldsId}.adults`} />
      <ChildrenAges fieldsId={`${fieldsId}.childrenAges`} />
    </Box>
  );
};

const CircularActionButton = ({ onClick, disabled, children }: { onClick: () => void; disabled?: boolean; children: React.ReactNode }) => {
  return (
    <Flex
      alignItems="center"
      justifyContent="center"
      border="1px solid"
      borderColor={disabled ? 'mono6' : 'newUIBrandPrimary'}
      borderRadius="full"
      onClick={disabled ? undefined : onClick}
      width={pxToRem(22)}
      height={pxToRem(22)}
      padding={0}
      opacity={disabled ? 0.7 : 1}
      backgroundColor={disabled ? 'mono1' : 'white'}
      color={disabled ? 'mono6' : 'newUIBrandPrimary'}
      cursor={disabled ? 'not-allowed' : 'pointer'}
      _hover={
        disabled
          ? undefined
          : {
              backgroundColor: 'mono2',
              borderColor: 'newUIBrandPrimary',
              color: 'newUIBrandPrimary',
            }
      }
      _active={
        disabled
          ? undefined
          : {
              backgroundColor: 'mono3',
              borderColor: 'newUIBrandPrimary',
              color: 'newUIBrandPrimary',
            }
      }
    >
      {children}
    </Flex>
  );
};

const NumAdults = ({ fieldsId }: { fieldsId: string }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  const [field, _, helper] = useField<IRoomSchema['adults']>(fieldsId);
  const handleAddAdult = () => {
    helper.setValue(field.value + 1);
  };
  const handleRemoveAdult = () => {
    helper.setValue(field.value - 1);
  };
  return (
    <Box
      display={'grid'}
      gridTemplateColumns={'auto 1fr'}
      alignItems={'center'}
      gap={4}
    >
      <Flex
        flexDirection="column"
        gap={1}
      >
        <Box typographyVariant={'hed2'}>{tq`Adults`}</Box>
        <Box
          as="span"
          color="mono8"
          typographyVariant={'label4'}
          fontWeight={400}
        >
          {tq`Age 18 or above`}
        </Box>
      </Flex>
      <Box
        display={'flex'}
        gap={3}
        alignItems={'center'}
        justifyContent={'flex-end'}
      >
        <CircularActionButton
          onClick={handleRemoveAdult}
          disabled={field.value <= 1}
        >
          <Subtract size={16} />
        </CircularActionButton>
        <Box
          typographyVariant={'body2'}
          minWidth={4}
          textAlign="center"
        >
          {field.value}
        </Box>
        <CircularActionButton
          onClick={handleAddAdult}
          disabled={field.value >= 6}
        >
          <Add size={16} />
        </CircularActionButton>
      </Box>
    </Box>
  );
};

const ChildrenAges = ({ fieldsId }: { fieldsId: string }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  const [field, _, helper] = useField<IRoomSchema['childrenAges']>(fieldsId);
  const handleAddChild = () => {
    helper.setValue([...field.value, null]);
  };
  const handleRemoveChild = () => {
    helper.setValue(field.value.slice(0, -1));
  };
  return (
    <Box
      display={'grid'}
      gridTemplateColumns={'1fr'}
      gap={4}
    >
      <Box
        display={'grid'}
        gridTemplateColumns={'auto 1fr'}
        alignItems={'center'}
        gap={4}
      >
        <Flex
          flexDirection="column"
          gap={1}
        >
          <Box typographyVariant={'hed2'}>{tq`Children`}</Box>
          <Box
            as="span"
            color="mono8"
            typographyVariant={'label4'}
            fontWeight={400}
          >
            {tq`Age 17 or under`}
          </Box>
        </Flex>
        <Box
          display={'flex'}
          gap={3}
          alignItems={'center'}
          justifyContent={'flex-end'}
        >
          <CircularActionButton
            onClick={handleRemoveChild}
            disabled={field.value.length === 0}
          >
            <Subtract size={16} />
          </CircularActionButton>
          <Box
            typographyVariant={'body2'}
            minWidth={4}
            textAlign="center"
          >
            {field.value.length}
          </Box>
          <CircularActionButton
            onClick={handleAddChild}
            disabled={field.value.length >= 6}
          >
            <Add size={16} />
          </CircularActionButton>
        </Box>
      </Box>
      {field.value.length > 0 && (
        <>
          {field.value.map((_, index) => (
            <ChildAge
              key={index}
              fieldsId={`${fieldsId}[${index}]`}
              childNumber={index + 1}
            />
          ))}
        </>
      )}
    </Box>
  );
};

const ChildAge = ({ fieldsId, childNumber }: { fieldsId: string; childNumber: number }) => {
  const { tq } = useTranslation('locationDatesOccupants');
  const [field, meta, helper] = useField<number | null>(fieldsId);
  const { setFieldError } = useFormikContext();

  const shouldShowError = meta.touched && !!meta.error && (field.value === null || typeof field.value === 'undefined');
  const errorProps = {
    isInvalid: shouldShowError,
    error: shouldShowError && typeof meta.error === 'string' ? meta.error : undefined,
  };

  return (
    <Box
      width={'full'}
      marginBottom={3}
    >
      <Flex
        justifyContent={'space-between'}
        width={'full'}
        alignItems={'center'}
        gap={4}
        marginBottom={1}
      >
        <Flex
          flexDirection="column"
          gap={1}
        >
          <Box typographyVariant={'hed2'}>{tq`Child ${childNumber}`}</Box>
          <Box
            as="span"
            color="mono8"
            typographyVariant={'label4'}
            fontWeight={400}
          >
            {tq`Age 17 or under`}
          </Box>
        </Flex>
        <Box maxWidth={pxToRem(160)}>
          <StyledAgeSelect
            value={field.value ?? ''}
            onChange={e => {
              const value = e.target.value;
              if (value === '') {
                helper.setValue(null);
                helper.setTouched(true);
                return;
              }
              const ageValue = parseInt(value, 10);
              helper.setValue(ageValue);
              helper.setTouched(true);
              setFieldError(fieldsId, undefined);
            }}
            aria-invalid={errorProps.isInvalid}
          >
            <option value="">{tq`Select Age`}</option>
            {AGE_OPTIONS.map(age => (
              <option
                key={age}
                value={age}
              >
                {age === 0 ? tq`Under 1` : age}
              </option>
            ))}
          </StyledAgeSelect>
        </Box>
      </Flex>
      {errorProps.isInvalid && errorProps.error && (
        <Box
          typographyVariant={'label2'}
          color={'red6'}
          textAlign="right"
          marginTop={1}
        >
          {errorProps.error}
        </Box>
      )}
    </Box>
  );
};

export { Fields, initialValue, createSchema };
